import React, { useContext, useState } from 'react';
import { Button, Form, Table, Container, Row, Col } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { IdentityContext } from '../../../../../../../../../context/identity-context';

const ReportInfo = ({ formData, saveData, nextStep }) => {
    const { user } = useContext(IdentityContext);
    const { applicationId, isin, interestPaymentDate } = useParams();
    const history = useHistory();
    
    const [localData, setLocalData] = useState({
        applicationId: applicationId,
        clientName: formData.clientName || "",
        bpId: formData.bpId || "",
        isin: isin,
        esipReferenceNumber: formData.esipReferenceNumber || "",
        documents: formData.documents,
        amount: formData.amount || "",
        rejectionHistory: formData.rejectionHistory,
        sellingAgentEmail: user.attributes['email'],
        interestPaymentDate: interestPaymentDate
    });
    
    const [errors, setErrors] = useState({});

    // Helper function to format numbers with commas and up to 2 decimal places
    const formatWithCommas = (value) => {
        let [integerPart, decimalPart] = value.split(".");
        if (integerPart) {
            integerPart = parseInt(integerPart, 10).toLocaleString("en-US");
        }
        if (value.includes(".")) {
            decimalPart = (decimalPart || "").slice(0, 2);
            return decimalPart.length > 0 ? `${integerPart}.${decimalPart}` : `${integerPart}.`;
        }
        return integerPart;
    };

    // Use our own function for amount formatting that enforces input rules
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;

        if (name === 'clientName') {
            newValue = newValue.slice(0, 250);
        }

        if (name === 'bpId') {
            // Allow only alphanumeric characters and limit to 11 characters.
            newValue = newValue.replace(/[^a-zA-Z0-9]/g, '').slice(0, 11);
        }

        if (name === 'esipReferenceNumber') {
            // Allow only alphanumeric characters and '-' as special character, limit to 100 characters.
            newValue = newValue.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 100);
        }

        if (name === 'amount') {
            let rawValue = newValue.replace(/,/g, '').slice(0, 100);
            // Allow empty string so deletion works correctly.
            if (rawValue === "") {
                newValue = "";
            } else if (/^\d+(\.?\d{0,2})?$/.test(rawValue)) {
                newValue = formatWithCommas(rawValue);
            } else {
                newValue = localData.amount;
            }
        }

        setLocalData({ ...localData, [name]: newValue });
        // Clear previous error for this field if any.
        setErrors({ ...errors, [name]: "" });
    };

    const handleNext = () => {
        let newErrors = {};

        if (!localData.clientName.trim()) {
            newErrors.clientName = "Client's Name is required";
        }
        if (!localData.bpId) {
            newErrors.bpId = "bpId is required";
        } else if (localData.bpId.length !== 11) {
            newErrors.bpId = "bpId must be exactly 11 characters";
        }
        if (!localData.esipReferenceNumber) {
            newErrors.esipReferenceNumber = "e-SIP Reference Number is required";
        } else if (!/^[a-zA-Z0-9-]*$/.test(localData.esipReferenceNumber)) {
            newErrors.esipReferenceNumber = "e-SIP Reference Number must be alphanumeric and can only include '-' as a special character";
        }
        if (!localData.amount) {
            newErrors.amount = "Amount is required";
        } else {
            const rawAmount = localData.amount.replace(/,/g, '');
            if (!/^\d+(\.?\d{0,2})?$/.test(rawAmount)) {
                newErrors.amount = "Amount must be a number with up to two decimal places";
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        saveData(localData);
        nextStep(false, localData);
    };

    const handleBack = () => {
        history.push('/tax-exempt-reports');
    };

    return (
        <>
            <Container className="report-container mb-3">
                <h2>Enter Security Details</h2>
                <Row className="mb-4 px-5">
                    <Col>
                        <Form>
                            {/* First Row */}
                            <Row className="d-flex justify-content-between">
                                <Col className="col-6">
                                    <Form.Group>
                                        <Form.Label>Client's Name <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="clientName"
                                            value={localData.clientName}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.clientName}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.clientName}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col className="col-4">
                                    <Form.Group>
                                        <Form.Label>bpId <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="bpId"
                                            value={localData.bpId}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.bpId}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.bpId}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="d-flex justify-content-between">
                                <Col className="col-4">
                                    <Form.Group>
                                        <Form.Label>e-SIP Reference Number <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="esipReferenceNumber"
                                            value={localData.esipReferenceNumber}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.esipReferenceNumber}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.esipReferenceNumber}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col className="col-4">
                                    <Form.Group>
                                        <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="amount"
                                            value={localData.amount}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.amount}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.amount}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>
                    </Col>
                </Row>
            </Container>

            <Container className="report-container mb-3">
                <Row className="mb-4">
                    <Col>
                        <h3>Rejection History</h3>
                        <Table striped bordered>
                            <thead>
                                <tr>
                                    <th>Rejection</th>
                                    <th>By</th>
                                    <th>Remark</th>
                                    <th>Date of Rejection</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData.rejectionHistory.map((entry, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{entry.rejectedBy}</td>
                                        <td>{entry.reason}</td>
                                        <td>{entry.dateOfRejection}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Container>
            <Container className="mb-5">
                <Row>
                    <Col className="d-flex justify-content-between">
                        <Button variant="secondary" onClick={handleBack}>
                            Back
                        </Button>
                        <Button variant="primary" onClick={handleNext}>
                            Next
                        </Button>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default ReportInfo;
